import { Link, useNavigate } from 'react-router-dom';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { Button } from '../components/Button/Button';
import { GalleryCoverArt } from './GalleryCoverArt';
import { flattenInfinite, useGalleryApps } from './useGallery';
import type { GalleryPublishedAppEntry } from './galleryApi';
import './GalleryPage.css';

/**
 * Standalone live-apps page.
 *
 * `/gallery` shows a tiny preview of live apps + a CTA pointing here.
 * This page is the full collection: paginated grid, cross-link back
 * to remix kits, and explicit "not remixable" copy on every card so
 * the difference between trained kits and shipped apps stays clear.
 */
export function GalleryAppsPage(): JSX.Element {
  const navigate = useNavigate();
  const apps = useGalleryApps();

  const items = flattenInfinite<GalleryPublishedAppEntry>(apps.data);
  const total = apps.data?.pages[0]?.total ?? 0;

  return (
    <div className="bl-page">
      <AppHeader
        subBrand="Live apps"
        privacy="published"
        back={{ label: 'Back to gallery', to: '/gallery' }}
      />
      <div className="bl-gallery">
        <section className="bl-gallery__hero">
          <h1 className="bl-gallery__title">Live apps</h1>
          <p className="bl-gallery__lead">
            Mini-apps kids deployed. Open one in a new tab to try the live experience.{' '}
            <strong>These can&rsquo;t be remixed</strong> — the creator shipped the finished thing,
            not the dataset behind it. To remix a model,{' '}
            <Link to="/gallery">browse the remix kits →</Link>
          </p>
        </section>

        {apps.error && (
          <p className="bl-gallery__error" role="alert">
            {apps.error.message}
          </p>
        )}

        {apps.isPending && (
          <p className="bl-gallery__status" aria-live="polite">
            Loading live apps…
          </p>
        )}

        {!apps.isPending && items.length === 0 && (
          <section className="bl-gallery__empty" aria-live="polite">
            <h2>No live apps yet</h2>
            <p>
              Train a model in the studio, hit <strong>Ship</strong> to deploy it as a mini-app on
              bryta.app, and it&rsquo;ll show up here.
            </p>
            <Button onClick={() => navigate('/studio')}>Open the studio</Button>
          </section>
        )}

        {items.length > 0 && (
          <>
            <p className="bl-gallery__count-line" aria-live="polite">
              Showing {items.length.toLocaleString()} of {total.toLocaleString()}.
            </p>
            <ul className="bl-gallery__grid bl-gallery__grid--apps">
              {items.map((app) => (
                <AppTile key={app.id} app={app} />
              ))}
            </ul>
            {apps.hasNextPage && (
              <div className="bl-gallery__more">
                <button
                  type="button"
                  className="bl-gallery__more-btn"
                  onClick={() => void apps.fetchNextPage()}
                  disabled={apps.isFetchingNextPage}
                >
                  {apps.isFetchingNextPage
                    ? 'Loading…'
                    : `Show me more (${(total - items.length).toLocaleString()} left)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <AppFooter />
    </div>
  );
}

function AppTile({ app }: { app: GalleryPublishedAppEntry }): JSX.Element {
  return (
    <li className="bl-gallery__card bl-gallery__card--app">
      <a
        href={app.share_url}
        target="_blank"
        rel="noopener noreferrer"
        className="bl-gallery__card-cover"
        aria-label={`Open ${app.title} in a new tab`}
      >
        <GalleryCoverArt name={app.title} kind="app" labels={[]} />
      </a>
      <div className="bl-gallery__card-body">
        <a
          href={app.share_url}
          target="_blank"
          rel="noopener noreferrer"
          className="bl-gallery__card-name"
        >
          {app.title} <span aria-hidden="true">↗</span>
        </a>
        <p className="bl-gallery__card-kind">live mini-app · v{app.version}</p>
        <p className="bl-gallery__card-classes">{app.view_count.toLocaleString()} visits</p>
        <p className="bl-gallery__card-date">
          Shipped {new Date(app.published_at).toLocaleDateString()}
        </p>
        <p className="bl-gallery__card-no-remix">
          Not remixable — the live app doesn&rsquo;t share its dataset.
        </p>
        {app.sibling_submission_id && (
          <Link
            to={`/gallery/${app.sibling_submission_id}`}
            className="bl-gallery__sibling"
            title="The same kid published the underlying model — you can remix that"
          >
            🧩 Remix the underlying kit
          </Link>
        )}
        <div className="bl-gallery__card-actions">
          <a
            href={app.share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="bl-gallery__card-open"
          >
            Open
          </a>
        </div>
      </div>
    </li>
  );
}
